import React, { useState, useEffect } from "react";
import { getSettings, saveSettings } from "../lib/api";
import { PRICING, defaultDuration } from "../mock/mock";
import { useToast } from "../hooks/use-toast";
import { Save, Clock, Users } from "lucide-react";

export default function SchedulingPanel() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({ workStart: "11:00", workEnd: "20:30", slotMinutes: 30, staffCount: 5, services: {}, subDurations: {} });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSettings().then((s) => setCfg({
      workStart: s.workStart || "11:00",
      workEnd: s.workEnd || "20:30",
      slotMinutes: s.slotMinutes || 30,
      staffCount: s.staffCount || 5,
      services: s.services || {},
      subDurations: s.subDurations || {},
    })).catch(() => {});
  }, []);

  const setTop = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setCap = (cat, v) => setCfg((c) => ({ ...c, services: { ...c.services, [cat]: { ...(c.services[cat] || {}), dailyCap: v === "" ? 0 : parseInt(v, 10) || 0 } } }));
  const durKey = (cat, sub, name) => `${cat}|${sub}|${name}`;
  const getDur = (cat, sub, name) => {
    const k = durKey(cat, sub, name);
    return cfg.subDurations[k] != null ? cfg.subDurations[k] : defaultDuration(cat, sub, name);
  };
  const setDur = (cat, sub, name, v) => setCfg((c) => ({ ...c, subDurations: { ...c.subDurations, [durKey(cat, sub, name)]: parseInt(v, 10) || 0 } }));

  const save = async () => {
    setBusy(true);
    try {
      // ensure every sub-service has a stored duration
      const subDurations = { ...cfg.subDurations };
      PRICING.forEach((t) => t.groups.forEach((g) => g.items.forEach(([name]) => {
        const k = durKey(t.cat, g.sub, name);
        if (subDurations[k] == null) subDurations[k] = defaultDuration(t.cat, g.sub, name);
      })));
      await saveSettings({ capacity: cfg.staffCount, slotMinutes: parseInt(cfg.slotMinutes, 10) || 30, workStart: cfg.workStart, workEnd: cfg.workEnd, staffCount: parseInt(cfg.staffCount, 10) || 1, services: cfg.services, subDurations });
      toast({ title: "Scheduling saved \u2728" });
    } catch (e) {
      toast({ title: "Save failed", description: "Please try again." });
    }
    setBusy(false);
  };

  const inp = "bg-viimii border hairline rounded-lg px-3 py-2 text-cream focus:outline-none focus:border-gold w-full";

  return (
    <div className="max-w-4xl">
      {/* Global */}
      <div className="p-6 rounded-2xl border hairline bg-viimii-2 mb-6">
        <h3 className="font-display text-xl text-cream mb-1 flex items-center gap-2"><Clock size={18} /> Studio Hours & Staff</h3>
        <p className="text-sm text-cream/55 mb-5">Slots are generated within these hours based on how many staff can work at once.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><label className="text-xs text-cream/55 block mb-1.5">Opening</label><input type="time" className={inp} value={cfg.workStart} onChange={(e) => setTop("workStart", e.target.value)} /></div>
          <div><label className="text-xs text-cream/55 block mb-1.5">Closing</label><input type="time" className={inp} value={cfg.workEnd} onChange={(e) => setTop("workEnd", e.target.value)} /></div>
          <div><label className="text-xs text-cream/55 block mb-1.5">Slot step (min)</label><input type="number" className={inp} value={cfg.slotMinutes} onChange={(e) => setTop("slotMinutes", e.target.value)} /></div>
          <div><label className="text-xs text-cream/55 block mb-1.5 flex items-center gap-1"><Users size={12} /> Staff on duty</label><input type="number" className={inp} value={cfg.staffCount} onChange={(e) => setTop("staffCount", e.target.value)} /></div>
        </div>
      </div>

      {/* Per therapy + sub-service durations */}
      <div className="space-y-5">
        {PRICING.map((t) => (
          <div key={t.cat} className="p-6 rounded-2xl border hairline bg-viimii-2">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b hairline">
              <h3 className="font-display text-xl text-cream">{t.cat}</h3>
              <div className="flex items-center gap-2">
                <label className="text-xs text-cream/55">Max bookings / day</label>
                <input type="number" placeholder="\u221e" className="bg-viimii border hairline rounded-lg px-3 py-1.5 text-cream w-20 focus:outline-none focus:border-gold" value={(cfg.services[t.cat]?.dailyCap) || ""} onChange={(e) => setCap(t.cat, e.target.value)} />
              </div>
            </div>
            {t.groups.map((g) => (
              <div key={g.sub} className="mb-4 last:mb-0">
                <p className="text-[11px] tracking-widest uppercase mb-2" style={{ color: "var(--viimii-clay)" }}>{g.sub}</p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                  {g.items.map(([name]) => (
                    <div key={name} className="flex items-center justify-between gap-3 py-1">
                      <span className="text-sm text-cream/75 truncate">{name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <input type="number" className="bg-viimii border hairline rounded-lg px-2.5 py-1.5 text-cream w-20 text-right focus:outline-none focus:border-gold" value={getDur(t.cat, g.sub, name)} onChange={(e) => setDur(t.cat, g.sub, name, e.target.value)} />
                        <span className="text-xs text-cream/45">min</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      <button onClick={save} disabled={busy} className="mt-6 inline-flex items-center gap-2 text-black font-medium px-7 py-3.5 rounded-full hover:opacity-90 transition-opacity" style={{ background: "var(--viimii-gold)" }}>
        <Save size={16} /> {busy ? "Saving..." : "Save Scheduling"}
      </button>
    </div>
  );
}
